import { FormControlLabel, Radio, RadioGroup } from "@mui/material"
import Input from "../Input/Input"
import React, { useEffect, useState } from "react"
import { formatCreditCardNumber, formatCVC, formatExpirationDate } from "../../utils/payment"
import {
  addCardPayment,
  addCreditCardByLink
} from "../../features/payment/paymentSlice"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { REQUEST_ERRORS_SCREENS, REQUEST_ERRORS_CARDS_DEBTS } from "../../constants/requestErrorFlags"

const refusalMessageOf = (response) => (typeof response?.payload === 'string'
  ? response.payload
  : (response?.payload?.message || 'Failed to save card. Please try again.'))

const PaymentCardViewTab = ({
                              updList,
                              address,
                              triggerUpd,
                              onClose,
                              userId,
                              onSuccessFunc,
                              onNextStep,
                              setFields,
                              setErrors,
                              errors,
                              isLink = true,
                              removeOld = false,
                              onCardSaved,
                              reportRefusals = false,
                              onRefused,
                              onSaving,
                            }) => {
  const dispatch = useDispatch()
  const screensOn = useSelector(selectFeatureFlag(REQUEST_ERRORS_SCREENS))
  const cardsDebtsOn = useSelector(selectFeatureFlag(REQUEST_ERRORS_CARDS_DEBTS))
  const [cardData, setCardData] = useState({
    cardNumber: "",
    nameOnCard: "",
    expiry: "",
    cvv: "",
    provider: "",
    expiryMonth: "",
    expiryDate: "",
  })

  useEffect(() => {
    setCardData({ ...cardData, ...address })
  }, [address])

  const fetchData = async () => {
    let started = false
    let saved = false
    try {
      if (!cardData?.cardNumber || !cardData?.nameOnCard || !cardData?.expiry || !cardData?.cvv
        || !cardData?.provider || !cardData?.expiryMonth || !cardData?.expiryDate) {
        return
      }
      started = true
      onSaving && onSaving(true)
      const response = isLink ?
        await dispatch(addCreditCardByLink({
          ...cardData,
          userId,
          removeOld
        })) : await dispatch(addCardPayment({ ...cardData, userId, isPrimary: false }))

      const refuse = reportRefusals || screensOn || cardsDebtsOn
      if (refuse && response?.meta?.requestStatus !== 'fulfilled') {
        showSnackBar("error", refusalMessageOf(response))
        onRefused && onRefused()
      } else if (response?.payload?.status === 400) {
        showSnackBar("error", response?.payload?.message)
      } else {
        showSnackBar("success", "Card add successfully")
        // The saved card's id and its verification session, when the API issued one. The card
        // screen needs both to run verification against the card that was just created.
        onCardSaved && onCardSaved(response?.payload?.data || null)
        onNextStep && onNextStep()
        onSuccessFunc && onSuccessFunc()

        updList && updList()
        onClose && onClose(false)
        saved = true
      }
    } catch (error) {
      showSnackBar("error", error)
      console.error('An unexpected error occurred while add payment card:', error)
    } finally {
      started && onSaving && onSaving(false, saved)
    }

  }
  useEffect(() => {
    if (triggerUpd) {
      fetchData()
    }
  }, [triggerUpd])

  const handleInputChange = ({ target }) => {
    let value = target.value
    let newCardData = { ...cardData }

    if (value) {
      setErrors && setErrors((prevErrors) => ({
        ...prevErrors,
        [target.name]: false,
      }))
    }

    if (target.name === "cardNumber") {
      value = formatCreditCardNumber(value, newCardData.provider)
    } else if (target.name === "expiry") {
      value = formatExpirationDate(value)
      newCardData.expiryMonth = value.substring(0, 2)
      newCardData.expiryDate = value.substring(3, 5)
    } else if (target.name === "cvv") {
      value = formatCVC(value, { number: newCardData.cardNumber, provider: newCardData.provider })
    } else if (target.name === "provider") {
      newCardData.cardNumber = formatCreditCardNumber(newCardData.cardNumber, value)
      newCardData.cvv = formatCVC(newCardData.cvv, { number: newCardData.cardNumber, provider: value })
      setFields && setFields((prev) => ({
        ...prev,
        cardNumber: newCardData.cardNumber,
        cvv: newCardData.cvv,
      }))
    }

    newCardData[target.name] = value
    setFields && setFields((prev) => ({ ...prev, [target.name]: value }))
    setCardData(newCardData)
  }

  return (
    <div className="w-full">
      <div className="flex">
        <RadioGroup
          className="flex justify-between gap-2"
          row
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="provider"
          value={cardData.provider}
          onChange={(e) => handleInputChange(e)}
        >
          <FormControlLabel value="Visa" control={<Radio/>} label="Visa"/>
          <FormControlLabel value="Mastercard" control={<Radio/>} label="Mastercard"/>
          <FormControlLabel value="American Express" control={<Radio/>} label="American Express"/>
          <FormControlLabel value="Discover" control={<Radio/>} label="Discover"/>
        </RadioGroup>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Input
            label="Name of Card"
            placeholder="Enter Name of Card"
            value={cardData.nameOnCard}
            type="text"
            name="nameOnCard"
            className="form-control"
            required
            onChange={handleInputChange}

          />
          <Input
            label="Card Number"
            value={cardData.cardNumber}
            type="tel"
            name="cardNumber"
            className="form-control"
            placeholder="•••• •••• •••• ••••"
            required
            onChange={handleInputChange}
            errorMsg={
              cardData.cardNumber && errors?.cardNumberType === 'invalidPrefix'
                ? errors?.cardNumber
                : undefined
            }
          />
          <Input
            label="CVV"
            value={cardData.cvv}
            type="tel"
            name="cvv"
            className="form-control"
            placeholder="•••"
            required
            onChange={handleInputChange}
          />
          <Input
            label="Expire Date"
            value={cardData.expiry}
            type="tel"
            name="expiry"
            className="form-control"
            placeholder="10/25"
            required
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  )
}
export default PaymentCardViewTab
